// V177: Invitation Cache Service
// Loads invitation data on app startup and caches it with 1-day TTL
// Refreshes on every app load

import { simpleCache } from '@/lib/simpleCache';
import { generalInvitationService } from './generalInvitationService';
import { supabase } from '@/lib/supabaseGuard';

export interface CachedInvitationData {
  inviteCode: string;
  inviteLink: string;
  inviteMessage: string;
  maxUses: number;
  currentUses: number;
  remaining: number;
  templateKey: string;
  cachedAt: number;
}

const CACHE_KEY_PREFIX = 'user_invitation_';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 1 day

function hasCorruptedInviteText(value: string): boolean {
  // Detect both real replacement chars and common mojibake representation.
  return value.includes('\uFFFD') || value.includes('ï¿½');
}

class InvitationCacheService {
  private static instance: InvitationCacheService;

  public static getInstance(): InvitationCacheService {
    if (!InvitationCacheService.instance) {
      InvitationCacheService.instance = new InvitationCacheService();
    }
    return InvitationCacheService.instance;
  }

  /**
   * Get cache key for user
   */
  private getCacheKey(userId: string): string {
    return `${CACHE_KEY_PREFIX}${userId}`;
  }

  // Request deduplication: Map<userId, Promise>
  private pendingLoads = new Map<string, Promise<CachedInvitationData | null>>();

  /**
   * Load invitation data from database and cache it
   * Called on app startup
   * V177: Uses 1-day TTL, only refreshes if cache expired, prevents duplicate creation
   */
  async loadAndCacheInvitation(userId: string): Promise<CachedInvitationData | null> {
    try {
      console.log('🔄 V177: Loading invitation data for user:', userId);

      // Check cache first - use 1-day TTL (don't clear on every load)
      const cacheKey = this.getCacheKey(userId);
      const cached = this.getCachedInvitation(userId);
      
      if (cached) {
        console.log('✅ V177: Using cached invitation (1-day TTL):', cached.inviteCode);
        return cached;
      }

      // V177: Request deduplication - prevent race conditions
      if (this.pendingLoads.has(userId)) {
        console.log('⏳ V177: Invitation load already in progress, waiting...');
        return await this.pendingLoads.get(userId)!;
      }

      // Start loading
      const loadPromise = this.performLoadAndCache(userId);
      this.pendingLoads.set(userId, loadPromise);
      
      try {
        const result = await loadPromise;
        return result;
      } finally {
        this.pendingLoads.delete(userId);
      }
    } catch (error) {
      console.error('💥 V177: Exception loading invitation:', error);
      this.pendingLoads.delete(userId);
      return null;
    }
  }

  /**
   * Internal method to perform the actual load and cache
   */
  private async performLoadAndCache(userId: string): Promise<CachedInvitationData | null> {
    try {
      const cacheKey = this.getCacheKey(userId);

      // V177: Fetch invitation from database (ensure only one active invitation)
      // First, let the service handle cleanup of duplicates and get/create invitation
      const createResult = await generalInvitationService.createGeneralInvitation(userId);
      
      if (!createResult.success || !createResult.inviteCode) {
        console.error('❌ V177: Failed to get/create invitation:', createResult.error);
        return null;
      }

      // V300: Fetch the invitation data (RLS-compliant: filter by creator_id)
      const { data: invitations, error } = await supabase
        .from('general_invitations')
        .select('invite_code, max_uses, current_uses, template_key, created_at')
        .eq('creator_id', userId)
        .eq('invite_code', createResult.inviteCode)
        .eq('is_active', true)
        .limit(1);

      if (error) {
        console.error('❌ V177: Error fetching invitation:', error);
        return null;
      }

      // Get invitation from array
      const invitation = Array.isArray(invitations) && invitations.length > 0 ? invitations[0] : null;

      if (!invitation) {
        console.error('❌ V177: Invitation not found after creation');
        return null;
      }

      // Build invite link and message
      const inviteLink = createResult.inviteLink || `https://blissnet.app/invite/${invitation.invite_code}`;
      const inviteMessage = await generalInvitationService.getInvitationShareMessage(invitation.invite_code);

      const cachedData: CachedInvitationData = {
        inviteCode: invitation.invite_code,
        inviteLink,
        inviteMessage: inviteMessage || '',
        maxUses: invitation.max_uses || 10000, // Always 10,000, never change
        currentUses: invitation.current_uses || 0,
        remaining: (invitation.max_uses || 10000) - (invitation.current_uses || 0),
        templateKey: invitation.template_key || 'whatsapp_default',
        cachedAt: Date.now()
      };

      // Cache with 1-day TTL (don't clear on load, only expires after 1 day)
      simpleCache.set(cacheKey, cachedData, CACHE_TTL_MS);
      console.log('✅ V177: Invitation loaded and cached (1-day TTL):', cachedData.inviteCode);
      return cachedData;

    } catch (error) {
      console.error('💥 V177: Exception in performLoadAndCache:', error);
      return null;
    }
  }

  /**
   * Get cached invitation data for user
   * Returns null if not cached or expired (1-day TTL)
   * V177: Respects 1-day TTL, only refreshes after expiration
   */
  getCachedInvitation(userId: string): CachedInvitationData | null {
    const cacheKey = this.getCacheKey(userId);
    const cached = simpleCache.get<CachedInvitationData>(cacheKey);
    
    if (!cached) {
      return null;
    }

    // Guard against stale corrupted cache entries from older bad payloads.
    if (hasCorruptedInviteText(cached.inviteMessage || '')) {
      simpleCache.delete(cacheKey);
      return null;
    }

    // Check if cache is still valid (within 1-day TTL)
    const age = Date.now() - cached.cachedAt;
    if (age > CACHE_TTL_MS) {
      console.log('⏰ V177: Cached invitation expired (1-day TTL), will refresh on next access');
      simpleCache.delete(cacheKey);
      return null;
    }

    return cached;
  }

  /**
   * Clear cached invitation for user
   */
  clearCache(userId: string): void {
    const cacheKey = this.getCacheKey(userId);
    simpleCache.delete(cacheKey);
    console.log('🧹 V177: Cleared invitation cache for user:', userId);
  }

  /**
   * Refresh invitation data and update cache
   */
  async refreshInvitation(userId: string): Promise<CachedInvitationData | null> {
    this.clearCache(userId);
    return await this.loadAndCacheInvitation(userId);
  }
}

export const invitationCacheService = InvitationCacheService.getInstance();

